export default class CheckoutCountryGuard {
	constructor(container) {
		this.container = container;
		this.card = container.querySelector('[data-country-guard]');
		if (!this.card) return;
		this.market = (this.card.dataset.cgMarket || '').trim().toUpperCase();
		this.links = this.parseLinks(this.card.dataset.cgLinks || '');
		this.countryTargets = this.card.querySelectorAll('[data-cg-country]');
		this.variantMarket = this.card.querySelector('[data-cg-variant="market"]');
		this.variantSupport = this.card.querySelector('[data-cg-variant="support"]');
		this.link = this.card.querySelector('[data-cg-link]');
		this.fields = ['checkout-address', 'checkout-postcode', 'checkout-city']
			.map((name) => container.querySelector(`input[name="${name}"]`))
			.filter(Boolean);
		if (!this.market || this.fields.length < 2) return;

		this.seq = 0;
		this.timer = null;
		this.fields.forEach((field) => field.addEventListener('input', () => this.onInput()));
		// A blocked submit does nothing visible (validateForm bails on .has-error),
		// so bring the card into view when the customer tries anyway.
		container.querySelectorAll('button[type="submit"]').forEach((button) => {
			button.addEventListener('click', () => {
				if (this.isActive()) this.card.scrollIntoView({ behavior: 'smooth', block: 'center' });
			});
		});
		this.onInput();
	}

	isActive() {
		return !this.card.classList.contains('d-none');
	}

	onInput() {
		// Fail-open while typing: any edit clears the block until re-verified.
		this.hide();
		clearTimeout(this.timer);
		this.timer = setTimeout(() => this.check(), 700);
	}

	value(name) {
		const field = this.fields.find((f) => f.name === name);
		return field ? field.value.trim() : '';
	}

	async check() {
		const address = this.value('checkout-address');
		const postcode = this.value('checkout-postcode');
		const city = this.value('checkout-city');
		// Verify only complete addresses: all three fields, each at least 3 characters.
		if (address.length < 3 || postcode.length < 3 || city.length < 3) return;

		const query = [address, postcode, city].filter(Boolean).join(', ');
		const seq = ++this.seq;
		const country = await this.resolveCountry(query);
		if (seq !== this.seq) return;
		if (!country) return; // fail-open
		if (country === this.market) return;
		this.show(country);
	}

	// Resolves the typed address to a country via the Places API (New) - the
	// SAME library the address autocomplete uses, and the only Google API
	// activated on this key (the Geocoding API is not enabled). No region
	// restriction on purpose - we want the true country, not a market-biased
	// guess. Fail-open on every miss.
	async resolveCountry(query) {
		if (!window.google || !window.google.maps || typeof window.google.maps.importLibrary !== 'function') return null;
		try {
			const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
			if (!AutocompleteSuggestion) return null;
			const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query });
			if (!suggestions || !suggestions.length || !suggestions[0].placePrediction) return null;
			const place = suggestions[0].placePrediction.toPlace();
			await place.fetchFields({ fields: ['addressComponents'] });
			const components = place.addressComponents || [];
			const component = components.find((c) => c.types.includes('country'));
			if (!component || !component.shortText) return null;
			return component.shortText.toUpperCase();
		} catch (error) {
			return null;
		}
	}

	show(countryCode) {
		const name = this.countryName(countryCode);
		this.countryTargets.forEach((el) => { el.textContent = name; });
		const url = this.links[countryCode];
		if (url && this.variantMarket && this.link) {
			this.link.href = url;
			this.link.textContent = url.replace(/^https?:\/\//, '');
			this.variantMarket.classList.remove('d-none');
			if (this.variantSupport) this.variantSupport.classList.add('d-none');
		} else {
			if (this.variantMarket) this.variantMarket.classList.add('d-none');
			if (this.variantSupport) this.variantSupport.classList.remove('d-none');
		}
		this.card.classList.remove('d-none');
		this.card.classList.add('has-error');
	}

	hide() {
		this.card.classList.add('d-none');
		this.card.classList.remove('has-error');
	}

	countryName(code) {
		try {
			const lang = document.documentElement.lang || 'en';
			return new Intl.DisplayNames([lang], { type: 'region' }).of(code) || code;
		} catch (error) {
			return code;
		}
	}

	parseLinks(raw) {
		const map = {};
		raw.split(/\r?\n/).forEach((line) => {
			const idx = line.indexOf('=');
			if (idx < 1) return;
			const code = line.slice(0, idx).trim().toUpperCase();
			const url = line.slice(idx + 1).trim();
			if (code && url) map[code] = url;
		});
		return map;
	}
}
